import React, { useEffect, useState } from "react";
import { authApi, providerApi, toApiAbsoluteUrl } from "../../api/client";
import { timeAgo } from "../../utils/time";
import { MapPin } from "lucide-react";
import { Chip } from "@mui/material";
import { useAuth } from "../../context/AuthContext";

export default function ProviderHome() {
  const { user } = useAuth();
  const [meApi, setMeApi] = useState(null);
  const [myListings, setMyListings] = useState([]);
  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        try {
          const res = await authApi.me();
          setMeApi(res || null);
        } catch (_e) {
          setMeApi(null);
        }
        try {
          const prof = await providerApi.getMyProfile();
          setMyProfile(prof || null);
        } catch (_e) {
          setMyProfile(null);
        }
        const mine = await providerApi.myListings();
        setMyListings(mine?.items || []);
      } catch (_e) {
        setMyListings([]);
      }
    })();
  }, [user]);

  const mergedUser = meApi || user || {};
  const providerProfile =
    myProfile?.providerProfile || mergedUser?.providerProfile || {};

  const pick = (...keys) => {
    for (const k of keys) {
      const fromPP = providerProfile?.[k];
      if (fromPP !== undefined && fromPP !== null && String(fromPP) !== "")
        return fromPP;
      const fromUser = mergedUser?.[k];
      if (
        fromUser !== undefined &&
        fromUser !== null &&
        String(fromUser) !== ""
      )
        return fromUser;
    }
    return "";
  };

  const typeLabel = (t) => {
    switch (String(t || "").toLowerCase()) {
      case "manufacturer":
        return "Manufacturer";
      case "wholesaler":
        return "Wholesaler";
      case "distributor":
        return "Distributor";
      case "supplier":
        return "Supplier";
      case "industrial_services":
        return "Industrial Services";
      case "exporters":
        return "Exporters";
      case "traders":
        return "Traders";
      case "other":
        return "Other";
      default:
        return t || "-";
    }
  };

  const profileFields = [
    ["Email", pick("email")],
    ["Company Name", pick("companyName")],
    ["Phone", pick("phoneNumber", "phone")],
    ["GST Number", pick("gstNumber")],
    ["PAN Number", pick("panNumber")],
    ["Address", pick("address")],
    ["Company Type", typeLabel(pick("companyType"))],
  ];

  const statusLabel = (status) => {
    switch (status) {
      case "pending_approval":
        return "Pending Approval";
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      case "archived":
        return "Archived";
      default:
        return status || "-";
    }
  };

  const statusBadgeClass = (status) => {
    switch (status) {
      case "pending_approval":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "archived":
        return "bg-gray-200 text-gray-800";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div>
      <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">
        Provider Dashboard
      </h2>

      {mergedUser && (mergedUser.email || providerProfile) && (
        <section className="w-full">
          <div className="bg-white rounded-md shadow ring-1 ring-gray-100 p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Account Overview
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {profileFields.map(([label, value]) => (
                <div key={label}>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
                    {label}
                  </div>
                  <div
                    className="text-gray-900 text-base font-medium truncate"
                    title={String(value || "-")}
                  >
                    {String(value || "-")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="mt-10">
        <h3 className="text-2xl font-bold mb-4 text-gray-800 text-center">
          Recent Listings
        </h3>

        <div className="bg-white shadow rounded overflow-x-auto">
          <table className="w-full text-center min-w-[1000px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border">Image</th>
                <th className="p-2 border">Name</th>
                <th className="p-2 border">Price</th>
                <th className="p-2 border">Category</th>
                <th className="p-2 border">Subcategory</th>
                <th className="p-2 border">Address</th>
                <th className="p-2 border">Radius (km)</th>
                <th className="p-2 border">Location</th>
                <th className="p-2 border">Status</th>
                <th className="p-2 border">Created</th>
              </tr>
            </thead>
            <tbody>
              {myListings.slice(0, 10).map((it) => {
                const imageUrls = Array.isArray(it?.images)
                  ? it.images
                      .map((img) => (typeof img === "string" ? img : img?.url))
                      .filter(Boolean)
                  : [];
                const firstImage =
                  imageUrls.length > 0 ? toApiAbsoluteUrl(imageUrls[0]) : null;
                const placeholder = `https://placehold.co/80x60/png?text=${encodeURIComponent(
                  (it?.name || "No Image").slice(0, 10)
                )}`;
                return (
                  <tr key={it._id || it.id}>
                    <td className="p-2 border">
                      <img
                        src={firstImage || placeholder}
                        alt={it.name}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = placeholder;
                        }}
                        className="w-14 h-10 object-cover rounded"
                      />
                    </td>
                    <td className="p-2 border">{it.name}</td>
                    <td className="p-2 border">
                      {typeof it.price === "number"
                        ? new Intl.NumberFormat("en-IN", {
                            style: "currency",
                            currency: "INR",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(it.price)
                        : "-"}
                    </td>
                    <td className="p-2 border">
                      <Chip
                        label={it.category?.name || "-"}
                        size="small"
                        variant="outlined"
                      />
                    </td>
                    <td className="p-2 border">
                      <Chip
                        label={it.subcategory?.name || "-"}
                        size="small"
                        variant="outlined"
                      />
                    </td>
                    <td className="p-2 border">{it.address || "-"}</td>
                    <td className="p-2 border">{it.maxRadiusKm ?? "-"}</td>
                    <td className="p-2 border">
                      {Array.isArray(it?.location?.coordinates)
                        ? (() => {
                            const [lng, lat] = it.location.coordinates;
                            const url = `https://www.google.com/maps?q=${lat},${lng}`;
                            return (
                              <button
                                onClick={() =>
                                  window.open(url, "_blank", "noopener")
                                }
                                className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                title={`Open in Maps (${lat}, ${lng})`}
                              >
                                <MapPin size={18} />
                              </button>
                            );
                          })()
                        : "-"}
                    </td>
                    <td className="p-2 border">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass(
                          it.status
                        )}`}
                      >
                        {statusLabel(it.status)}
                      </span>
                    </td>
                    <td className="p-2 border">
                      {it.createdAt ? (
                        <span title={new Date(it.createdAt).toLocaleString()}>
                          {timeAgo(it.createdAt)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })}
              {myListings.length === 0 && (
                <tr>
                  <td className="p-3 text-gray-500" colSpan={10}>
                    No listings yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
